import React, { useEffect, useState } from 'react';
import { RecoilState, useRecoilState } from 'recoil';
import { Box, Grid } from '@mui/material';
import { Slice } from 'features/morphing/state/parameterAtoms';
import { CustomTypography } from 'shared/ui/CustomTypography';
import { CustomNumberField } from 'shared/ui/CustomNumberField';

interface Props {
  sliceState: RecoilState<Slice>;
  label: string;
  maxValue: number;
  labelXs?: number;
  fieldXs?: number;
  backgroundColor?: string;
}

export const SliceForm: React.FC<Props> = ({
  sliceState,
  label,
  maxValue,
  labelXs = 4,
  fieldXs = 3,
  backgroundColor = "#1e1e1e",
}) => {
  const [slice, setSlice] = useRecoilState(sliceState);
  const [start, setStart] = useState<string>(String(slice.start));
  const [stop, setStop] = useState<string>(String(slice.stop));

  useEffect(() => {
    setStart(String(slice.start));
    setStop(String(slice.stop));
  }, [slice]);

  const handleChange = (axis: 'start' | 'stop') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    // Update the corresponding local state
    if (axis === 'start') setStart(inputValue);
    if (axis === 'stop') setStop(inputValue);

    // Parse the input value as a number
    const numericValue = parseInt(inputValue, 10);

    // If the input is a valid number, update the vector state
    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= maxValue) {
      setSlice(prevSlice => ({
        ...prevSlice,
        [axis]: numericValue,
      }));
    }
  };

  return (
    <Box component="div" sx={{ paddingTop: 1 }}>
      <Grid container alignItems="center" spacing={0.5}>
        <Grid item xs={labelXs}>
          <CustomTypography>
            {label}
          </CustomTypography>
        </Grid>
        <Grid item xs={fieldXs}>
          <CustomNumberField
            required
            label="start"
            type="number"
            fullWidth
            value={start}
            onChange={handleChange('start')}
            inputProps={{ min: 1, max: slice.stop }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: backgroundColor,
              }
            }
            }
          />
        </Grid>
        <Grid item xs={fieldXs}>
          <CustomNumberField
            required
            label="stop"
            type="number"
            fullWidth
            value={stop}
            onChange={handleChange('stop')}
            inputProps={{ min: slice.start, max: maxValue }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: backgroundColor,
              }
            }
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};
